import dayjs, {Dayjs} from 'dayjs';
import {getWeekRange} from '@utils/date';

export type CheckinDeadlineInput = {
  dueDate?: string;
  dueAt?: string;
  dueDay?: string;
  weekOfYear?: number;
  year?: number;
  status?: string;
};

export type CheckinDeadline = {
  dueDate?: Dayjs;
  isOverdue: boolean;
  daysOverdue: number;
};

const weekdayOffsets: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

const parseDate = (value?: string) => {
  if (!value) return undefined;
  const date = dayjs(value);
  return date.isValid() ? date : undefined;
};

export const resolveCheckinDueDate = ({
  dueDate,
  dueAt,
  dueDay,
  weekOfYear,
  year,
}: CheckinDeadlineInput): Dayjs | undefined => {
  const explicitDate = parseDate(dueDate) || parseDate(dueAt);
  if (explicitDate) return explicitDate;

  const offset = dueDay ? weekdayOffsets[dueDay.toLowerCase()] : undefined;
  if (offset !== undefined && weekOfYear && year) {
    return dayjs(getWeekRange(weekOfYear, year).starts).add(offset, 'day');
  }

  // Legacy rows may store an actual date in dueDay instead of a weekday.
  return dueDay && /^\d{4}-\d{2}-\d{2}/.test(dueDay)
    ? parseDate(dueDay)
    : undefined;
};

export const getCheckinDeadline = (
  checkin: CheckinDeadlineInput,
  now = dayjs(),
): CheckinDeadline => {
  const dueDate = resolveCheckinDueDate(checkin);
  const isOverdue = dueDate
    ? now.isAfter(dueDate.endOf('day'))
    : checkin.status?.toLowerCase() === 'overdue';

  return {
    dueDate,
    isOverdue,
    daysOverdue:
      isOverdue && dueDate
        ? Math.max(1, now.startOf('day').diff(dueDate.startOf('day'), 'day'))
        : 0,
  };
};

export const mergeCheckinDeadline = (
  current: CheckinDeadlineInput,
  fallback?: CheckinDeadlineInput,
): CheckinDeadlineInput => ({
  dueDate: current.dueDate || fallback?.dueDate,
  dueAt: current.dueAt || fallback?.dueAt,
  dueDay: current.dueDay || fallback?.dueDay,
  weekOfYear: current.weekOfYear || fallback?.weekOfYear,
  year: current.year || fallback?.year,
  status:
    fallback?.status?.toLowerCase() === 'overdue'
      ? fallback.status
      : current.status || fallback?.status,
});
